// services/sugerenciaCitaService.ts

import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';
import { SolicitudReserva } from './solicitudReservaService';

const API_URL = 'sugerencias-citas/';

// Tipo correspondiente al serializer
export interface SugerenciaCita {
    id: number;
    cliente: number;
    cliente_nombre: string;
    cita_referencia: number | null;
    solicitud_reserva?: number | null;

    fecha_inicio: string;
    fecha_fin: string;
    cantidad_periodos: number | null;
    hora_inicio: string;
    hora_fin: string;

    servicio_sugerido: number;
    servicio_id: number | null;
    servicio_sugerido_nombre: string;
    plan_nombre: string | null;

    espacio_servicio: number | null;
    espacio_servicio_nombre: string | null;

    categoria: number | null;
    categoria_nombre: string | null;

    servicio_sugerido_imagen: string | null;
    espacio_servicio_imagen: string | null;

    subtotal: number | null;
    descuento_sugerido: number | null;
    porcentaje_descuento_promocional?: number | null;
    total: number | null;

    cupon_asociado: number | null;
    cupon_codigo: string | null;

    motivo: string;
    vista: boolean;
    fecha_generada: string;

    // Nuevo: expuesto por el serializer para saber si el plan usa temporizador
    usa_temporizador: boolean;
    descartada?: boolean;
}

// GET: Obtener todas las sugerencias
export const fetchSugerencias = async (): Promise<SugerenciaCita[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<SugerenciaCita[]>(API_URL);
        return response.data;
    });
};

export const fetchSugerenciasByUsuario = async (userId: number): Promise<SugerenciaCita[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<SugerenciaCita[]>(API_URL, {
            params: { cliente: userId }
        });
        return response.data;
    });
};

// POST: Crear una sugerencia manualmente (opcional para admin)
export const createSugerencia = async (
    data: Omit<
        SugerenciaCita,
        'id' | 'cliente_nombre' | 'servicio_sugerido_nombre' |
        'plan_nombre' | 'espacio_servicio_nombre' | 'cupon_codigo' |
        'fecha_generada' | 'usa_temporizador'
    >
) => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(API_URL, data);
        return response.data;
    });
};

// PUT: Marcar sugerencia como vista
export const updateSugerencia = async (
    id: number,
    data: Partial<SugerenciaCita>
) => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.put(`${API_URL}${id}/marcar_como_vista/`, data);
        return response.data;
    });
};

// DELETE: Eliminar sugerencia
export const deleteSugerencia = async (id: number) => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.delete(`${API_URL}${id}/`);
        return response.data;
    });
};

/**
 * Error específico para cuando falta algún dato obligatorio en la sugerencia
 * y por lo tanto no se puede convertir en cita sin intervención manual.
 */
export class SugerenciaInvalidaError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SugerenciaInvalidaError';
    }
}

export const reservarSugerencia = async (
    id: number
): Promise<{ status: string; solicitud: SolicitudReserva; nuevas_generadas?: number }> => {
    return handleRequestWithAuth(async () => {
        try {
            const response = await axiosInstance.post(`${API_URL}${id}/reservar/`);
            return response.data;
        } catch (error: any) {
            const mensaje =
                error?.response?.data?.detail ||
                (typeof error?.response?.data === 'string' ? error.response.data : null) ||
                'No se pudo completar la reserva. Intenta de nuevo.';
            throw new SugerenciaInvalidaError(mensaje);
        }
    });
};


export const completarSugerencias = async (): Promise<{
    status: string;
    nuevas_generadas: number;
}> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(`${API_URL}completar/`);
        return response.data;
    });
};


export type TipoInteraccion = 'impresion' | 'descartada' | 'click_detalle' | 'tiempo_visto';

export interface InteraccionPayload {
    tipo: TipoInteraccion;
    tiempo_visto_ms?: number; // requerido solo si tipo === 'tiempo_visto'
}

export const registrarInteraccion = async (
    id: number,
    payload: InteraccionPayload
): Promise<{ status: string; nuevas_generadas?: number }> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(`${API_URL}${id}/interaccion/`, payload);
        return response.data;
    });
};

